import {
  type RailMenuEntry,
  type RailMenuItem,
  type RailRow,
  type RailRowActionId,
  railRowMenu,
} from '@modules/common/components/rail-menu/rail-menu';
import { type VirtualFolder } from '@modules/notes/types/virtual-folder';

/** Whose Notes rail row a menu belongs to: the member's My Workspace or the Team Workspace. */
export type NotesRailScope = 'mine' | 'team';

/** A Notes rail row's key: the app's virtual folders plus the Shared header, which is not a folder. */
export type NotesRailKey = VirtualFolder | 'shared';

/** Notes' rail rows by the prototype's names. Rows not listed here keep their own menu. */
export const NOTES_RAIL_ROWS: Readonly<Partial<Record<NotesRailKey, RailRow>>> = {
  all_notes: 'all',
  all_notebooks: 'all',
  assigned_to_me: 'assigned',
  shared: 'shared',
  flagged: 'flagged',
  followed: 'followup',
  snoozed: 'snoozed',
  following: 'following',
  archived: 'archive',
  deleted: 'trash',
};

export interface NotesRowMenuContext {
  /** Why this person may not empty this Trash; absent when they may. */
  readonly purgeRefused?: string;
}

const TEAM_CLEAR_HINTS = {
  flagged: 'This removes flags for everyone in the team.',
  followed: 'This clears your follow-up reminders in this view. Other teammates’ reminders are unchanged.',
  snoozed: 'This clears your snoozes in this view. Other teammates’ reminders are unchanged.',
} as const;

const TEAM_CLEAR_TITLES = {
  flagged: 'Unflag all',
  followed: 'Remove all follow-ups',
  snoozed: 'Unsnooze all',
} as const;

export const PINS_SHARED_HINT = 'Pins in this workspace are shared by the team.';

/**
 * A Notes workspace rail row's menu: the prototype's entries from the shared definition, plus Notes' own
 * entries kept beside them (additive rule). `undefined` for a row the shared definition does not cover.
 */
export function notesRowMenu(
  key: NotesRailKey,
  scope: NotesRailScope,
  context: NotesRowMenuContext = {},
): readonly RailMenuItem[] | undefined {
  const row = NOTES_RAIL_ROWS[key];
  if (!row) return undefined;
  const team = scope === 'team';

  const live = new Set<RailRowActionId>(['new-item', 'new-container', 'trash-archive', 'empty-trash', 'settings']);
  if (team) {
    // Team flags are one shared state: the personal "Clear my flags" has nothing of its own to clear (Coming soon).
    if (key === 'followed' || key === 'snoozed' || key === 'following') live.add('clear');
  } else if (key === 'flagged' || key === 'followed' || key === 'snoozed') {
    live.add('clear');
  }

  const extras: RailMenuEntry[] = [];
  if (team && (key === 'flagged' || key === 'followed' || key === 'snoozed')) {
    extras.push({
      id: 'clear-team',
      title: TEAM_CLEAR_TITLES[key],
      icon: 'sapphire-close',
      hint: TEAM_CLEAR_HINTS[key],
    });
  }
  // My Following has no "Settings" in the old menu either; the team's Following always had one.
  if (key === 'archived') {
    extras.push({ id: 'delete-all', title: 'Delete all', icon: 'sapphire-trash', dangerous: true });
  }
  if (key === 'deleted') {
    extras.push({
      id: 'empty-trash-all',
      title: 'Empty trash',
      icon: 'sapphire-trash',
      dangerous: true,
      disabled: !!context.purgeRefused,
      reason: context.purgeRefused,
    });
  }

  return railRowMenu({
    row,
    itemNoun: 'note',
    containerNoun: 'notebook',
    readable: false,
    live,
    refused: context.purgeRefused ? { 'empty-trash': context.purgeRefused } : undefined,
    extras,
  });
}

/**
 * The menu of the Pinned/Team Knots or Tags row. The prototype's own entry manages the member's personal
 * pins, which a team's shared pins cannot be (Coming soon); Notes' Add and Manage stay beside it.
 */
export function notesPinnedMenu(kind: 'knots' | 'tags', scope: NotesRailScope): readonly RailMenuItem[] {
  const team = scope === 'team';
  return [
    { id: 'manage-pins', title: `Manage my pinned ${kind}`, icon: 'sapphire-settings', comingSoon: team },
    null,
    { id: 'add-meta', title: `Add ${kind}`, icon: 'sapphire-plus' },
    {
      id: 'manage-meta',
      title: `Manage ${kind}`,
      icon: 'sapphire-settings',
      hint: team ? PINS_SHARED_HINT : undefined,
    },
  ];
}
